/**
 * フッター会話テキストデータ
 *
 * RPG風の会話ボックスに表示するテキストを管理。
 * シーンごと・エラータイプごとに複数ページのテキスト配列を返す。
 *
 * 使用箇所:
 *   footer-dialogue-management.js - updateFooterDialogueText(), showFooterDialogue()
 *
 * データ構造:
 *   各シーン/エラータイプに対してstring[][]を返す（ページ配列 × 行配列）
 *   例: [["1ページ1行目", "1ページ2行目"], ["2ページ1行目"]]
 */
import { Scenes, NotificationTypes, CHARACTER_NAME } from "../constants.js";

const DEFAULT_FOOTER_DIALOGUE = [["収穫金倉庫へようこそ！"]];
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// 出金申請エラー共通テキスト（min/max/submit共通）
const PAYOUT_REQUEST_ERROR_DIALOGUE = [
  ["申し訳ないのじゃが、", "何やら上手く行っていないようじゃ。", "もう一度確認お願いしますぞ。"],
];

// TOPシーンの会話テキスト候補（ランダムで1つ選ばれる）
const topSceneDialogues = [
  [
    "収穫金倉庫へようこそ。",
    `わしは${CHARACTER_NAME}、何かご入用ですかな？`,
    "困ったことがあれば何でも聞いてくだされ。",
  ],
  ["収穫金倉庫に何かご入用ですかな？", "違う？わしに会いに来た？", "ほっほっほ、面白い方ですな。"],
  ["収穫金倉庫へようこそ。", "今日も一日良い日になりそうですぞ。"],
  [
    "あなたの収穫金倉庫を守っております。",
    `わしの名前は${CHARACTER_NAME}。`,
    "以後お見知りおきを。",
  ],
  ["メアリーをご存知ですかな？", "いつもあなたをサポートしてくれる", "蜂の名前でございますぞ。"],
  ["この老フクロウに何なりとご用命くだされ。", "収穫金倉庫のことでしたら", "何でもわかりますぞ。"],
  [
    "森のみんなも困ったことがあると、",
    "すぐわしのところへ聞きに来るのですぞ。",
    "あなたは何に困っているのですかな？",
  ],
  [
    "あなたの収穫金倉庫は",
    `この${CHARACTER_NAME}に安心してお任せください。`,
    "しっかりとお守りしますぞ。",
  ],
  [
    "わしに会いに来たのですか？",
    "ほっほっほ、ではわしの過去の話でも…",
    "いや、興味ないですよな、ほっほ。",
  ],
  [
    "収穫金倉庫はちゃんと見張っておりますぞ。",
    "今日は何の御用ですかな？",
    "何なりとお申し付けくだされ。",
  ],
];

/**
 * シーンに対応する会話テキスト配列を取得
 * @param {string} scene - Scenes定数の値
 * @param {Object} data - テンプレート内で使用する動的な値
 * @param {string} data.userAmount - ユーザー入力の出金額（フォーマット済み）
 * @param {boolean} data.hasInput - 出金申請画面で入力があるかどうか
 * @returns {string[][]} 会話テキスト配列（ページ × 行）
 */
export const buildFooterDialogueTexts = (scene, data) => {
  const { userAmount, hasInput } = data;

  const footerDialogueMap = {
    [Scenes.TOP]: [pickRandom(topSceneDialogues)],

    [Scenes.PAYOUT_REQUEST_TOP]: hasInput
      ? [["入力は済みましたかな？", "よろしかったら「申請する」ボタンを", "押してくだされ。"]]
      : [["出金申請ですな？", "出金希望金額を教えてくだされ。"]],

    [Scenes.PAYOUT_REQUEST_CONFIRM]: [
      [`${userAmount}円ですな？`, "よろしかったら「申請確定」ボタンを", "押してくだされ。"],
    ],

    [Scenes.HARVEST_TOP]: [
      [
        "収穫実績ですな？",
        "「収穫残高履歴」と「収穫予定一覧」が",
        "確認できますぞ。",
        "何でも聞いてくだされ。",
      ],
    ],
  };

  return footerDialogueMap[scene] || DEFAULT_FOOTER_DIALOGUE;
};

/**
 * 通知タイプに対応するダイアログテキストを取得
 * @param {string} notificationType - NotificationTypes定数の値
 * @returns {string[][]} 通知ダイアログテキスト配列（ページ × 行）
 */
export const buildNotificationDialogueTexts = (notificationType) => {
  const notificationDialogueMap = {
    [NotificationTypes.PAYOUT_REQUEST_TOP_MIN_ERROR]: PAYOUT_REQUEST_ERROR_DIALOGUE,
    [NotificationTypes.PAYOUT_REQUEST_TOP_MAX_ERROR]: PAYOUT_REQUEST_ERROR_DIALOGUE,
    [NotificationTypes.PAYOUT_REQUEST_CONFIRM_SUBMIT_SUCCESS]: [
      [
        "出金申請が完了しましたぞ。",
        "内容は出金申請画面の",
        "「出金申請履歴」ボタンから確認してくだされ。",
      ],
    ],
    [NotificationTypes.PAYOUT_REQUEST_CONFIRM_SUBMIT_ERROR]: PAYOUT_REQUEST_ERROR_DIALOGUE,
  };

  return notificationDialogueMap[notificationType] || null;
};
